import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const UserApprovalsTab = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const loadPending = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('user_approvals')?.select('*')?.eq('status', 'pending')?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading pending approvals:', error?.message, '| Code:', error?.code, '| Details:', error?.details);
        setPendingUsers([]);
      } else {
        console.log('Loaded pending approvals:', data?.length, 'records');
        // Map snake_case DB columns to camelCase for UI
        const mapped = (data || [])?.map(u => ({
          id: u?.id,
          name: u?.name,
          mobile: u?.mobile,
          email: u?.email,
          role: u?.role,
          company: u?.company,
          location: u?.location,
          requestedDate: u?.requested_date,
          status: u?.status,
        }));
        setPendingUsers(mapped);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (id) => {
    const user = pendingUsers?.find(u => u?.id === id);
    if (!user) return;

    const { error } = await supabase?.from('user_approvals')?.update({
        status: 'approved',
        approved_date: new Date()?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        updated_at: new Date()?.toISOString(),
      })?.eq('id', id);

    if (error) {
      showToast(`Failed to approve: ${error?.message}`, 'error');
      return;
    }

    await loadPending();
    showToast(`${user?.name} approved successfully`, 'success');
  };

  const handleReject = async (id) => {
    const user = pendingUsers?.find(u => u?.id === id);

    const { error } = await supabase?.from('user_approvals')?.update({
        status: 'rejected',
        updated_at: new Date()?.toISOString(),
      })?.eq('id', id);

    if (error) {
      showToast(`Failed to reject: ${error?.message}`, 'error');
      return;
    }

    await loadPending();
    showToast(`${user?.name || 'User'} request rejected`, 'error');
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium"
          style={{ backgroundColor: toast?.type === 'success' ? '#d1fae5' : '#fee2e2', color: toast?.type === 'success' ? '#065f46' : '#991b1b' }}
        >
          <Icon name={toast?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={16} />
          {toast?.msg}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>User Approval Requests</h2>
          <p className="text-sm mt-0.5" style={{ color: '#6b7280' }}>Review and approve new user registrations</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
          <Icon name="Clock" size={15} />
          {pendingUsers?.length} Pending
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading approval requests...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && pendingUsers?.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#f3f4f6' }}>
            <Icon name="UserCheck" size={28} color="#9ca3af" />
          </div>
          <h3 className="font-semibold mb-1" style={{ color: '#374151' }}>No Pending Requests</h3>
          <p className="text-sm" style={{ color: '#9ca3af' }}>All registration requests have been reviewed.</p>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && pendingUsers?.length > 0 && (
        <>
          <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#fef3c7' }}>
                  {['Name', 'Mobile', 'Email', 'Role', 'Company', 'Location', 'Requested Date', 'Actions']?.map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide" style={{ color: '#92400e' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingUsers?.map((user, idx) => (
                  <tr
                    key={user?.id}
                    className="border-t hover:bg-amber-50/30 transition-colors"
                    style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}
                  >
                    <td className="px-4 py-3 font-medium" style={{ color: '#1f2937' }}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#1e4a72' }}>
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </div>
                        {user?.name}
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ color: '#374151' }}>{user?.mobile || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td className="px-4 py-3" style={{ color: '#374151' }}>{user?.email || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: user?.role === 'Buyer' ? '#dbeafe' : '#fce7f3', color: user?.role === 'Buyer' ? '#1d4ed8' : '#9d174d' }}>
                        {user?.role}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: '#374151' }}>{user?.company || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td className="px-4 py-3" style={{ color: '#374151' }}>{user?.location || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#6b7280' }}>{user?.requestedDate}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(user?.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                          style={{ backgroundColor: '#059669' }}
                        >
                          <Icon name="Check" size={12} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(user?.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                          style={{ backgroundColor: '#dc2626' }}
                        >
                          <Icon name="X" size={12} />
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {pendingUsers?.map(user => (
              <div key={user?.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: '#1e4a72' }}>
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: '#1f2937' }}>{user?.name}</p>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: user?.role === 'Buyer' ? '#dbeafe' : '#fce7f3', color: user?.role === 'Buyer' ? '#1d4ed8' : '#9d174d' }}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: '#9ca3af' }}>{user?.requestedDate}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: '#6b7280' }}>
                  <div><span className="font-medium">Mobile:</span> {user?.mobile || '—'}</div>
                  <div><span className="font-medium">Email:</span> {user?.email || '—'}</div>
                  <div><span className="font-medium">Company:</span> {user?.company || '—'}</div>
                  <div><span className="font-medium">Location:</span> {user?.location || '—'}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(user?.id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white"
                    style={{ backgroundColor: '#059669' }}
                  >
                    <Icon name="Check" size={12} /> Approve
                  </button>
                  <button
                    onClick={() => handleReject(user?.id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white"
                    style={{ backgroundColor: '#dc2626' }}
                  >
                    <Icon name="X" size={12} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default UserApprovalsTab;
